import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Modal,
  SpaceBetween,
  Spinner,
  Table,
} from '@amzn/awsui-components-react';
import {
  RadarOrgAwsCustomerFlag,
  type RadarDirectionDetail,
  type RadarMemberNews,
  type RadarMemberOpp,
  type RadarReusableAsset,
  type RadarSuggestedOrgDetail,
} from '../../../client/IvtHubClient';
import { DENIAL_HINTS, type DenialKind } from '../../../utils/authorization';
import NewAssetCreateModal from '../../NewAssetsPage/components/NewAssetCreateModal';
import { useRadarDirectionAction } from '../useRadarDirectionAction';
import { formatArr } from './scale';
import { RadarFalsePositiveModal } from './RadarFalsePositiveModal';
import './RadarDirectionModal.css';

/**
 * Direction detail modal — Figma E `4833:15639` (DAT-9115, R18 + R19;
 * induction §5.1.1 / §5.2; XIN-ANSWERS §1 2026-10-29: a MODAL, not a drawer).
 *
 * Opened from a radar card title (R17 wired the Link to a no-op; this file
 * now receives that click through DashboardPage's `openDirectionId` state).
 * R19 wires the two footer actions:
 *   - 标记误报/不再展示 → RadarFalsePositiveModal picks the reason (and
 *     the covering asset when COVERED_BY_ASSET; XIN-ANSWERS §7); the
 *     ADOPTED path is NOT one of the reasons — a reason is a WHY it is
 *     NOT a direction.
 *   - 立项为新资产 → NewAssetCreateModal creates the asset, then this
 *     modal calls `UpdateRadarDirectionStatus(ADOPTED, adoptedAssetId)`
 *     with the id the create returned (induction §5.2 two-step; the
 *     backend handler does NOT create the asset).
 * On success the modal reports `onSuccess()` so the dashboard can drop
 * the card from the list and close the modal (an ADOPTED / FALSE_POSITIVE
 * row is no longer a `CANDIDATE`, which the dashboard scopes its
 * ListRadarDirections to — see useRadarDirections).
 *
 * Payload comes from `GetRadarDirection` through {@link useRadarDirection}.
 * The merged Smithy exposes memberOpps / memberNews / suggestedOrgs /
 * reusableAssets / needs; `gap_note` and `hint` (opp-extraction §5.3,
 * production §4.1) are induction-side columns that live only on
 * `radar_need_coverage` today and are NOT on the wire — the modal does
 * not render them in v1 (see DECISIONS.md "Open questions"). The three
 * tables below are what Figma E draws: member-opps (6 cols), member-news
 * (4 cols), suggested-orgs (5 cols). Reusable-assets is a one-line summary
 * under the title because Figma E does not give it a table — see §5.1
 * "一个方向有多个「可复用」时的展示规则在 §7 列为开放项".
 *
 * Why Cloudscape Table rather than a hand-rolled grid: Table carries the
 * accessible row header / column header semantics the Figma shows (visible
 * column labels with a sort chevron placeholder), and the empty slot has a
 * built-in `empty` prop that reads under screen reader without the empty-
 * list being announced as "no results in a list of zero". The three tables
 * share the modal's horizontal scroll container so a narrow viewport does
 * not clip them (DashboardPage sits inside IvtLayout's 1200px max width;
 * the modal's own `size="max"` lets Cloudscape go to the viewport width,
 * but a very long opp name would still overflow — the scroll is a floor).
 */
export interface RadarDirectionModalProps {
  /**
   * `null` while the parent has not yet opened the modal (first render), a
   * string once a card title has been clicked. The hook mirrors this: null
   * = idle, a string = fetch for that id. The parent decides when to unmount
   * so the hook can keep its last response across a quick close+reopen.
   */
  directionId: string | null;
  state: {
    data: RadarDirectionDetail | null;
    loading: boolean;
    error: string | null;
    denial: DenialKind | null;
    reload: () => void;
  };
  onDismiss: () => void;
  /**
   * Called after an FP or ADOPT write succeeds. The parent reloads the
   * dashboard radar list and closes the modal (CANDIDATE-only scope, so
   * the direction drops off the list — see RadarDirectionModal's header
   * comment).
   */
  onSuccess?: () => void;
}

/** Table empty-slot copy. Same voice as the radar card's "暂无…" phrasings. */
const EMPTY = {
  memberOpps: '本方向暂无管线成员',
  memberNews: '本方向暂无近期新闻',
  suggestedOrgs: '本方向暂无建议接触的机构客户',
};

function LastActivityCell({ date }: { date: string | undefined }) {
  // Nullable per smithy — an opp may have no activity recorded. Render an em
  // dash rather than a blank cell so the row still parses as "no date on
  // file" instead of "missing data".
  return <span>{date ?? '—'}</span>;
}

function ArrCell({ value }: { value: number | undefined }) {
  // ARR nullable in opportunity_cache; see RadarMemberOpp. formatArr already
  // handles the zero case ("$0"), but a truly absent ARR (undefined) is
  // different and must render as a dash so a reader does not read it as "0".
  if (value === undefined) return <span>—</span>;
  return <span>{formatArr(value)}</span>;
}

function NewsLinkCell({ item }: { item: RadarMemberNews }) {
  // Nullable: a manual upload may have no URL. Rendering a bare "原文" would
  // look like a dead link; render the outlet-less label fallback instead.
  // Scheme allowlist (http/https only) so an `item.url` carrying
  // `javascript:` / `data:` / another URI-handler scheme (news items are
  // external data per news design §2.6; the manual-upload path lets an
  // STO paste any string) cannot be clicked into a link-injection; the
  // `rel="noopener noreferrer"` + `target="_blank"` guards do NOT cover
  // scheme-level injection.
  const url = item.url;
  if (url === undefined || url === '' || !/^https?:\/\//i.test(url)) {
    return <span>—</span>;
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="radar-modal__news-link">
      原文
    </a>
  );
}

function OrgActionCell({ org }: { org: RadarSuggestedOrgDetail }) {
  // `action` column — Figma E draws a UI link; §5.1.1 says the action is
  // composed from `accountId` (SFDC account deep-link) when present and
  // skipped otherwise. The SFDC deep-link base URL is NOT configured in
  // this app (no deep-link helper exists; the surrounding pages all keep
  // SFDC navigation server-side), so v1 renders the account id as text
  // rather than a link that would 404 or open the wrong tenant — the
  // intent is kept, the hop isn't invented. Captured in DECISIONS.md
  // "Open questions" (SFDC deep-link base URL).
  if (org.accountId === undefined || org.accountId === '') return <span>—</span>;
  return <span className="radar-modal__org-action">{org.accountId}</span>;
}

function OrgAwsCell({ org }: { org: RadarSuggestedOrgDetail }) {
  // Both v1-absent per Xin 2026-10-29 and the smithy field comments: no AWS
  // tag, no deal count. The UI hides the sublabel entirely when both are
  // null — leaving a blank cell is already the empty state the table draws.
  // When a later rev starts populating the flag, map through the enum to
  // a Chinese label — never render `String(flag)` into the Chinese modal
  // (that would surface the raw enum value `AWS_CUSTOMER` / `NOT_AWS_CUSTOMER`).
  const flag = org.isAwsCustomer;
  const count = org.inDirectionDealCount;
  if (flag === undefined && count === undefined) return <span>—</span>;
  const flagText =
    flag === RadarOrgAwsCustomerFlag.AWS_CUSTOMER
      ? 'AWS 客户'
      : flag === RadarOrgAwsCustomerFlag.NOT_AWS_CUSTOMER
        ? '非 AWS 客户'
        : '';
  const countText = count === undefined ? '' : `本方向 ${count} 笔`;
  return <span>{[flagText, countText].filter((x) => x !== '').join(' · ')}</span>;
}

function ReusableAssetsRow({ assets }: { assets: readonly RadarReusableAsset[] }) {
  if (assets.length === 0) return null;
  // §5.1.1 and §5.1 "一个方向有多个「可复用」时的展示规则在 §7 列为开放项"
  // — Figma E does not give coverage its own table, so render the list
  // inline under the rationale. Each asset is one chip; the asset id is
  // not clickable in v1 because Rocky's Asset Detail route reads one asset
  // at a time keyed by assetId, but navigating off the modal would discard
  // scroll state on the dashboard — defer the link to a later row.
  return (
    <div className="radar-modal__coverage">
      <Box variant="small" color="text-body-secondary">
        可复用的资产(覆盖):
      </Box>
      <div className="radar-modal__coverage-chips">
        {assets.map((a, i) => (
          // Compose a stable key: assetId is `@required` on the smithy
          // (RadarReusableAsset) but surfaced as `string | undefined` on
          // the TS type; fall back to a composite that includes the row
          // index so two assets with the same name under different
          // industries never collide.
          <span key={`${a.assetId ?? a.assetName ?? ''}|${a.industry ?? ''}|${i}`} className="radar-modal__coverage-chip">
            <span>{a.assetName ?? a.assetId}</span>
            {a.industry !== undefined && a.industry !== '' && (
              <>
                <span className="radar-modal__coverage-sep">·</span>
                <span className="radar-modal__coverage-industry">{a.industry}</span>
              </>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

export function RadarDirectionModal({
  directionId,
  state,
  onDismiss,
  onSuccess,
}: RadarDirectionModalProps) {
  // The modal mounts/unmounts with the parent's `openDirectionId` state:
  // null = unmounted. So `directionId` here is always a string.
  // The parent now mounts this modal UNCONDITIONALLY (DashboardPage) so
  // `directionId` CAN be `null` even while the component is mounted — a
  // parent-driven dismiss during an inflight write would unwrap us. The
  // handlers below guard on `directionId === null` for that reason, and
  // `guardedDismiss` refuses the dismiss while any flow is live so the
  // orphan state (CreateAsset done, Update dropped) is unreachable.
  const { data, loading, error, denial, reload } = state;

  const header = data?.name ?? '方向详情';
  const rationale = data?.rationale ?? '';
  const pipe = data?.pipelineEvidence;
  const memberOpps = data?.memberOpps ?? [];
  const memberNews = data?.memberNews ?? [];
  const suggestedOrgs = data?.suggestedOrgs ?? [];
  const reusableAssets = data?.reusableAssets ?? [];

  // R19 action state. One in-flight call at a time: 标记误报 and 立项 are
  // mutually exclusive (both would mutate the direction's status), so one
  // hook state is enough.
  const action = useRadarDirectionAction();
  const [fpOpen, setFpOpen] = useState(false);
  const [adoptOpen, setAdoptOpen] = useState(false);
  // `adoptBusy` is set while UpdateRadarDirectionStatus(ADOPTED) is in
  // flight — RadarCreate wizard closes itself on CreateAsset success, so
  // without this flag the primary button briefly reads "not busy" between
  // CreateAsset returning and the Update starting.
  const [adoptBusy, setAdoptBusy] = useState(false);
  // The two-step adopt (induction §5.2) first CreateAssets, then
  // UpdateRadarDirectionStatus(ADOPTED, adoptedAssetId). If the SECOND
  // call fails, the asset was already written — the id must survive the
  // closed wizard so a retry can call the Update again INSTEAD of
  // opening the wizard again (which would create a duplicate asset).
  // Cleared on success and on explicit dismiss of the retry banner.
  const [pendingAdoptAssetId, setPendingAdoptAssetId] = useState<string | null>(null);
  // NewAssetCreateModal fires `onCreated(assetId)` on BOTH full success
  // (CreateAsset + LinkUcAsset / LinkTopicAsset) AND partial failure
  // (CreateAsset succeeded, Link failed — the wizard stays open to
  // surface its own error). We must only run the ADOPTED Update when
  // the asset was FULLY linked, otherwise we'd mark the direction
  // ADOPTED behind an unlinked asset. The wizard's own `onClose()` is
  // only called on the full-success branch, so we use it as the
  // "truly done" signal and read it from this ref inside `onCreated`.
  const wizardCreateCommittedRef = useRef(false);

  // The parent (DashboardPage) now mounts us unconditionally and only
  // toggles `visible` via `directionId`, so the previous "state is reset
  // by unmount" invariant no longer holds: an FP popup / retry banner /
  // error Alert left from direction A would persist into direction B.
  // The banner would even read A's `assetId` under B's name, which is
  // the kind of silent data mix-up a reviewer wouldn't catch at a
  // glance. Reset the per-direction state on every id change (null→id,
  // id→null, id→id'). `action.clearError` is stable (useCallback with
  // no deps), so leaving it out of the dep array is safe.
  //
  // Why we don't rely on `guardedDismiss` to clear this: `guardedDismiss`
  // runs when the user dismisses WITHOUT an inflight call, but it has
  // no way to know whether the next open will reuse the same id. The
  // id-change effect is the single source of reset.
  const lastDirectionIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastDirectionIdRef.current === directionId) return;
    lastDirectionIdRef.current = directionId;
    setFpOpen(false);
    setAdoptOpen(false);
    setAdoptBusy(false);
    setPendingAdoptAssetId(null);
    action.clearError();
  }, [directionId, action]);

  const canAct =
    directionId !== null && data !== null && !loading && denial === null;
  // COVERED_BY_ASSET requires the direction's industry to scope the asset
  // picker; without it ListAssets({}) would offer every industry's assets
  // and let a cross-industry id reach the server (which only enforces
  // presence of dismissAssetId, not industry match).
  const canFpOpen = canAct && data?.industry !== undefined && data.industry !== '';

  // Any time an in-flight call or an open child flow is holding state, the
  // outer modal MUST NOT be dismissible — otherwise the direction prop
  // flips to null while CreateAsset is still on the wire and the second
  // step is dropped silently (an orphaned asset).
  const isBusy = action.submitting || adoptBusy || adoptOpen;

  const closeFp = () => {
    if (action.submitting) return;
    setFpOpen(false);
    action.clearError();
  };

  const guardedDismiss = () => {
    if (isBusy) return;
    onDismiss();
  };

  const handleFpConfirm = async (payload: Parameters<typeof action.markFalsePositive>[1]) => {
    if (directionId === null) return;
    const ok = await action.markFalsePositive(directionId, payload);
    if (ok) {
      setFpOpen(false);
      onSuccess?.();
    }
  };

  // CreateAsset succeeded with assetId; run the Update. On failure, keep
  // the assetId so a retry re-fires UpdateRadarDirectionStatus(ADOPTED)
  // with the SAME id rather than creating a second asset.
  const runAdoptUpdate = async (assetId: string) => {
    if (directionId === null) return;
    setAdoptBusy(true);
    const ok = await action.adopt(directionId, assetId);
    setAdoptBusy(false);
    if (ok) {
      setPendingAdoptAssetId(null);
      onSuccess?.();
    } else {
      // Error path: the asset exists; stash the id for retry.
      setPendingAdoptAssetId(assetId);
    }
  };

  const handleAdoptCreated = async (assetId: string) => {
    // The wizard only calls onClose() on the full-success branch (and we
    // flipped the ref there). If this flag is still false, the wizard
    // hit its partial-failure path — CreateAsset ok, Link failed — and
    // is intentionally staying open to show its own error. Don't force-
    // close it and don't fire ADOPTED (would mark the direction ADOPTED
    // behind an unlinked asset). The user can retry inside the wizard,
    // or cancel it, in which case the orphaned-but-unlinked asset lives
    // on in the New Assets list, mirroring NewAssetCreateModal's own
    // keep-on-link-failure stance.
    if (!wizardCreateCommittedRef.current) return;
    wizardCreateCommittedRef.current = false;
    await runAdoptUpdate(assetId);
  };

  return (
    <Modal
      visible={directionId !== null}
      onDismiss={guardedDismiss}
      header={header}
      size="max"
      closeAriaLabel="关闭"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={guardedDismiss} disabled={isBusy}>
              Cancel
            </Button>
            {/* R19: both actions mutate direction status. Disabled while
                data is still loading, denied, or any sibling flow is
                live — see `isBusy`. FP additionally requires an
                industry (COVERED_BY_ASSET picker scope). */}
            <Button
              variant="normal"
              onClick={() => setFpOpen(true)}
              // Disabled while a pending-adopt retry is live too: an
              // FP write here would mark the direction FALSE_POSITIVE
              // and reload the dashboard, discarding the retry banner
              // and orphaning the asset CreateAsset already wrote (no
              // trace for the user). Dismissing the banner first
              // (which clears pendingAdoptAssetId) re-enables this.
              disabled={!canFpOpen || isBusy || pendingAdoptAssetId !== null}
            >
              标记误报/不再展示
            </Button>
            {pendingAdoptAssetId !== null ? (
              // After a successful CreateAsset + a failed Update, the
              // asset already exists — "立项" would create a second one.
              // Offer a direct retry of the Update instead.
              <Button
                variant="primary"
                onClick={() => runAdoptUpdate(pendingAdoptAssetId)}
                disabled={!canAct || isBusy}
                loading={adoptBusy}
              >
                重试立项
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={() => {
                  // Reset the "wizard confirmed success" flag on every
                  // open so a previous cancel's `onClose` cannot leave
                  // the ref armed and let a later partial-failure
                  // `onCreated` call auto-trigger the adopt Update.
                  wizardCreateCommittedRef.current = false;
                  setAdoptOpen(true);
                }}
                disabled={!canAct || isBusy}
                loading={adoptBusy}
              >
                立项为新资产
              </Button>
            )}
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {/* R19 adopt-side error — shown here (not in the FP popup) because
            the create wizard closes itself on CreateAsset success and the
            follow-up UpdateRadarDirectionStatus error has nowhere else to
            land. FP errors stay inside the FP popup. The header distinguishes
            the "asset was created, Update failed" state (so the STO knows
            not to click 立项 again — they'd write a duplicate asset; the
            footer offers 重试立项 instead for that path). */}
        {!fpOpen && action.error !== null && (
          <Alert
            type="error"
            header={pendingAdoptAssetId !== null ? '立项为新资产：资产已创建，但未能完成立项' : '立项为新资产失败'}
            dismissible
            onDismiss={() => {
              action.clearError();
              // Dismissing the pending-adopt banner means the STO
              // abandons the retry; clear the id so the primary button
              // reverts to 立项为新资产. The created asset survives in
              // the New Assets list (there is no CreateAsset-rollback
              // op), consistent with NewAssetCreateModal's keep-on-
              // link-failure stance.
              setPendingAdoptAssetId(null);
            }}
          >
            {pendingAdoptAssetId !== null ? (
              <>资产已创建（{pendingAdoptAssetId}），但立项失败：{action.error}。点击「重试立项」再次尝试，或关闭此条放弃本次立项（资产已存入新资产列表）。</>
            ) : (
              action.error
            )}
          </Alert>
        )}

        {loading && !data && !denial && (
          <div className="radar-modal__loading">
            <Spinner />
            <span>加载方向详情…</span>
          </div>
        )}

        {denial && (
          <Alert type="info" header="无权访问该方向">
            {DENIAL_HINTS[denial]}
          </Alert>
        )}

        {error && !loading && !denial && (
          <Alert
            type="error"
            header="无法加载方向详情"
            action={<Button onClick={reload}>重试</Button>}
          >
            {error}
          </Alert>
        )}

        {data && (
          <>
            {/* Rationale — the full paragraph (card truncates to 2 lines). */}
            <Box variant="p" color="text-body-secondary">
              {rationale}
            </Box>

            {/* Pipeline evidence summary — same triple as the card badge. */}
            <div className="radar-modal__pipeline">
              <span className="radar-modal__pipeline-heading">
                <span className="radar-modal__diamond">◆</span> 管线证据
              </span>
              <span className="radar-modal__pipeline-metric">
                <span className="radar-modal__pipeline-value">{pipe?.distinctAccounts ?? 0}</span>
                <span className="radar-modal__pipeline-suffix">家不同客户</span>
              </span>
              <span className="radar-modal__pipeline-metric">
                <span className="radar-modal__pipeline-value">{pipe?.oppCount ?? 0}</span>
                <span className="radar-modal__pipeline-suffix">笔交易</span>
              </span>
              <span className="radar-modal__pipeline-metric">
                <span className="radar-modal__pipeline-value">
                  {formatArr(pipe?.totalArrUsd ?? 0)}
                </span>
              </span>
              {(pipe?.technicalValidationAccountCount ?? 0) > 0 && (
                <span className="radar-modal__pipeline-metric">
                  <span className="radar-modal__pipeline-value">
                    {pipe?.technicalValidationAccountCount ?? 0}
                  </span>
                  <span className="radar-modal__pipeline-suffix">家处于 Technical Validation</span>
                </span>
              )}
            </div>

            <ReusableAssetsRow assets={reusableAssets} />

            {/* Member-opps table — Figma E 6 columns. `wrapLines` on so a
                long opp name wraps rather than being truncated with
                ellipsis — the modal is where readers go for the full
                story, not a summary. */}
            <Table<RadarMemberOpp>
              variant="embedded"
              wrapLines
              items={memberOpps as RadarMemberOpp[]}
              trackBy={(it) => it.opptyId ?? ''}
              header={<Box variant="h3">◆ 成员机会</Box>}
              empty={<Box color="text-body-secondary">{EMPTY.memberOpps}</Box>}
              columnDefinitions={[
                {
                  id: 'accountName',
                  header: 'Account',
                  cell: (it) => it.accountName ?? '',
                  width: 220,
                },
                {
                  id: 'oppName',
                  header: 'Opportunity',
                  cell: (it) => it.oppName ?? '',
                  width: 320,
                },
                {
                  id: 'arrUsd',
                  header: 'ARR',
                  cell: (it) => <ArrCell value={it.arrUsd} />,
                  width: 120,
                },
                {
                  id: 'lastActivityDate',
                  header: '最近活动',
                  cell: (it) => <LastActivityCell date={it.lastActivityDate} />,
                  width: 140,
                },
                {
                  id: 'stage',
                  header: '阶段',
                  cell: (it) => it.stage ?? '',
                  width: 160,
                },
                {
                  id: 'cell',
                  header: 'Cell',
                  cell: (it) => it.cell ?? '—',
                  width: 160,
                },
              ]}
            />

            {/* Member-news table — Figma E 4 cols. `wrapLines` on because
                `quoteSummary` is capped at 1024 chars and Cloudscape Table
                otherwise truncates with ellipsis (losing the reason the
                reader opened the modal); the CSS wrap class on the cell
                makes CJK-only strings break between code points. */}
            <Table<RadarMemberNews>
              variant="embedded"
              wrapLines
              items={memberNews as RadarMemberNews[]}
              // Nothing on-wire keys a news row uniquely (title / outlet /
              // publishedAt can repeat), and Cloudscape's TrackBy signature
              // is `(item) => string` with no index available. Compose a
              // deterministic key from the fields we do have — url when
              // present, else outlet + publishedAt + quote prefix. This is
              // stable per row content and acceptable to React: two rows
              // with identical content on-wire would be de-duped by the
              // server anyway.
              trackBy={(it) =>
                it.url ??
                `${it.outlet ?? ''}|${it.publishedAt ?? ''}|${(it.quoteSummary ?? '').slice(0, 32)}`
              }
              header={<Box variant="h3">◆ 新闻 · 报告出处(近 3 个月内)</Box>}
              empty={<Box color="text-body-secondary">{EMPTY.memberNews}</Box>}
              columnDefinitions={[
                {
                  id: 'outlet',
                  header: '媒体',
                  // Figma E's news table deliberately omits the S/M fetch-mode
                  // badge the card row carries — the smithy honors it:
                  // RadarMemberNews has no `fetchMode` field (RadarRecentNews
                  // does). So render outlet text alone.
                  cell: (it) => it.outlet ?? '',
                  width: 240,
                },
                {
                  id: 'quoteSummary',
                  header: '摘要引述',
                  // Wrap in a span with the news-quote class — the smithy
                  // caps `quoteSummary` at 1024 chars and CJK runs without
                  // spaces will overflow the cell on default CSS; the
                  // class forces `overflow-wrap: anywhere` so the quote
                  // breaks mid-string.
                  cell: (it) => (
                    <span className="radar-modal__news-quote">{it.quoteSummary ?? ''}</span>
                  ),
                  minWidth: 360,
                },
                {
                  id: 'url',
                  header: '原文',
                  cell: (it) => <NewsLinkCell item={it} />,
                  width: 100,
                },
                {
                  id: 'publishedAt',
                  header: '发布日期',
                  cell: (it) => it.publishedAt ?? '—',
                  width: 140,
                },
              ]}
            />

            {/* Suggested-orgs table — Figma E 5 cols. `wrapLines` on so a
                long `note` string (NAMED_IN_NEWS side joins all mention
                phrases with `;` and can be arbitrarily long per §5.3)
                wraps rather than clipping. */}
            <Table<RadarSuggestedOrgDetail>
              variant="embedded"
              wrapLines
              items={suggestedOrgs as RadarSuggestedOrgDetail[]}
              // accountId is unique when present (AWS_CUSTOMER_NO_PIPELINE
              // always resolves to one; NAMED_IN_NEWS may); otherwise
              // compose from side + name, which the two partial unique
              // indexes in §5.3 already guarantee is unique.
              trackBy={(it) => it.accountId ?? `${it.side ?? ''}|${it.name ?? ''}`}
              header={<Box variant="h3">◆ 建议接触的机构客户</Box>}
              empty={<Box color="text-body-secondary">{EMPTY.suggestedOrgs}</Box>}
              columnDefinitions={[
                {
                  id: 'name',
                  header: '机构',
                  cell: (it) => it.name ?? '',
                  width: 260,
                },
                {
                  id: 'note',
                  header: '说明',
                  cell: (it) => it.note ?? '',
                  minWidth: 320,
                },
                // §5.1.1 spec column list: `name, note, action, isAwsCustomer,
                // inDirectionDealCount` (5 cols). A standalone 来源 column
                // was invented in the first cut but duplicates whatever the
                // server already stamped in `note` ("AWS 客户 · 本方向 0 笔"
                // on the AWS side, mentioned-as phrases on the news side).
                // Dropped — matches Figma E's suggested-orgs row count.
                {
                  id: 'action',
                  header: '建议动作',
                  cell: (it) => <OrgActionCell org={it} />,
                  width: 220,
                },
                {
                  id: 'aws',
                  header: 'AWS 客户 · 本方向成交',
                  cell: (it) => <OrgAwsCell org={it} />,
                  width: 220,
                },
              ]}
            />
          </>
        )}
      </SpaceBetween>

      {/* R19 FP reason popup. Mounted conditionally so its buttons do NOT
          appear in the DOM while the popup is closed — Cloudscape Modal
          renders a hidden shell even at visible=false, and the hidden
          "取消" button there collides with other dashboard dialogs'
          "取消" in testing-library queries (DeleteLighthouseAccountModal
          uses the same word). Rendering it only while `fpOpen` keeps the
          popup out of unrelated queries and avoids a quiet regression
          across sibling dashboard tests. */}
      {fpOpen && (
        <RadarFalsePositiveModal
          visible
          industry={data?.industry}
          submitting={action.submitting}
          error={action.error}
          onDismiss={closeFp}
          onConfirm={handleFpConfirm}
        />
      )}

      {/* R19 adopt wizard — reuses NewAssetsPage's create modal. When the
          create succeeds we immediately call UpdateRadarDirectionStatus with
          the new assetId (two-step, induction §5.2). The wizard is mounted
          conditionally so the GetSolutionMapAll it fires on open is paid
          only when the user actually opens it. */}
      {adoptOpen && (
        <NewAssetCreateModal
          onClose={() => {
            if (adoptBusy) return;
            // NewAssetCreateModal only calls onClose() on the full-
            // success path (not on explicit Cancel of the wizard's
            // own overlay click — that path routes through onClose
            // too, so the flip here is reversed if `onCreated` does
            // not immediately follow; `handleAdoptCreated` reads the
            // flag and resets it anyway). Either way, flipping true
            // here is correct: the only path where onCreated fires
            // WITHOUT onClose first is the partial-failure branch,
            // which we want to catch — and the only way the ref is
            // true when onCreated fires is the full-success path.
            wizardCreateCommittedRef.current = true;
            setAdoptOpen(false);
          }}
          onCreated={handleAdoptCreated}
        />
      )}
    </Modal>
  );
}
